import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../AuthContext';
import { useOrgFeatures } from './useOrgFeatures';
import { useVersionedApi } from '../utils/versionedApi';

const NO_AI_ENTITLEMENTS = Object.freeze({
  aiWritingAssist: false,
  aiSupervisoryReview: false,
});

/**
 * Server-authoritative AI seat entitlements for the signed-in user.
 *
 * Role permissions alone are intentionally insufficient: older/custom Admin
 * roles can still carry AI permission keys even though an Administrative seat
 * is not a paid clinical AI seat. The status endpoint combines permission,
 * billed provider type, and the narrow WeCare compatibility exemption.
 */
export function useAiEntitlements() {
  const apiClient = useVersionedApi('v1');
  const { user } = useAuth() || {};
  const { hasExternalIntegration, loading: integrationsLoading } = useOrgFeatures();
  const integrationEnabled = hasExternalIntegration('azureAi');

  const query = useQuery({
    queryKey: ['ai-clinical-entitlements', user?.organizationId, user?.id || user?.userId],
    queryFn: async () => {
      const response = await apiClient.get('/ai/clinical/status');
      return response.data?.entitlements || NO_AI_ENTITLEMENTS;
    },
    enabled: Boolean(user && !integrationsLoading && integrationEnabled),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const entitlements = integrationEnabled && query.data
    ? query.data
    : NO_AI_ENTITLEMENTS;

  return {
    ...entitlements,
    loading: integrationsLoading || (integrationEnabled && query.isPending),
  };
}

export default useAiEntitlements;
